// Client Claude avec fallback automatique AWS Bedrock + API Anthropic
import { BedrockRuntimeClient, InvokeModelCommand } from '@aws-sdk/client-bedrock-runtime'

interface ClaudeMessage {
  role: 'user' | 'assistant' | 'system'
  content: string
}

interface ClaudeResponse {
  content: Array<{ text: string }>
}

class ClaudeClient {
  private bedrockClient: BedrockRuntimeClient | null = null
  private anthropicKey: string | null = null
  private selectedModel: string | null = null
  private useDirectAPI: boolean = false

  constructor() {
    this.initialize()
  }

  private initialize() {
    // Configuration AWS Bedrock
    const awsAccessKey = import.meta.env.VITE_AWS_ACCESS_KEY_ID
    const awsSecretKey = import.meta.env.VITE_AWS_SECRET_ACCESS_KEY
    const awsRegion = import.meta.env.VITE_AWS_REGION || 'us-east-1'

    // Configuration API Anthropic directe
    this.anthropicKey = import.meta.env.VITE_ANTHROPIC_API_KEY

    if (this.anthropicKey) {
      console.log('🤖 Claude: Utilisation API Anthropic directe')
      this.useDirectAPI = true
      this.selectedModel = import.meta.env.VITE_CLAUDE_MODEL || 'claude-3-5-sonnet-20241022'
    } else if (awsAccessKey && awsSecretKey) {
      console.log('🤖 Claude: Utilisation AWS Bedrock')
      this.bedrockClient = new BedrockRuntimeClient({
        region: awsRegion,
        credentials: {
          accessKeyId: awsAccessKey,
          secretAccessKey: awsSecretKey,
        },
      })
      this.selectedModel = import.meta.env.VITE_CLAUDE_MODEL || 'arn:aws:bedrock:us-east-1:335158495031:inference-profile/us.anthropic.claude-sonnet-4-6'
    } else {
      throw new Error('Configuration Claude manquante: configurez VITE_ANTHROPIC_API_KEY ou les clés AWS')
    }
  }

  async generateResponse(
    messages: ClaudeMessage[],
    systemPrompt?: string,
    maxTokens: number = 2000,
    temperature: number = 0.3
  ): Promise<string> {
    if (this.useDirectAPI && this.anthropicKey) {
      return await this.callAnthropicAPI(messages, systemPrompt, maxTokens, temperature)
    } else {
      return await this.callAWSBedrock(messages, systemPrompt, maxTokens, temperature)
    }
  }

  private async callAnthropicAPI(
    messages: ClaudeMessage[],
    systemPrompt?: string,
    maxTokens: number = 2000,
    temperature: number = 0.3
  ): Promise<string> {
    try {
      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': this.anthropicKey!,
          'anthropic-version': '2023-06-01'
        },
        body: JSON.stringify({
          model: this.selectedModel,
          max_tokens: maxTokens,
          temperature,
          system: systemPrompt,
          messages: messages.map(msg => ({
            role: msg.role === 'system' ? 'user' : msg.role,
            content: msg.content
          }))
        })
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`API Anthropic error: ${response.status} - ${errorText}`)
      }

      const result = await response.json()
      return result.content[0].text
    } catch (error) {
      console.error('❌ Erreur API Anthropic:', error)
      throw new Error(`Erreur Claude API: ${error}`)
    }
  }

  private async callAWSBedrock(
    messages: ClaudeMessage[],
    systemPrompt?: string,
    maxTokens: number = 2000,
    temperature: number = 0.3
  ): Promise<string> {
    if (!this.bedrockClient || !this.selectedModel) {
      throw new Error('Client AWS Bedrock non initialisé')
    }

    // Configuration optimale : Sonnet 4.6 via inference profile + fallback Haiku
    const modelsToTry = [
      'arn:aws:bedrock:us-east-1:335158495031:inference-profile/us.anthropic.claude-sonnet-4-6', // Sonnet 4.6 via ARN
      this.selectedModel,                              // Modèle configuré
      'anthropic.claude-3-haiku-20240307-v1:0',       // Fallback Haiku 3 garanti
    ].filter(Boolean)

    let lastError: Error | null = null

    for (const modelId of modelsToTry) {
      try {
        console.log(`🧪 Test du modèle AWS Bedrock: ${modelId}`)

        const requestBody = {
          anthropic_version: "bedrock-2023-05-31",
          max_tokens: maxTokens,
          temperature,
          system: systemPrompt,
          messages: messages.filter(msg => msg.role !== 'system').map(msg => ({
            role: msg.role,
            content: msg.content
          }))
        }

        const command = new InvokeModelCommand({
          modelId,
          contentType: "application/json",
          accept: "application/json",
          body: JSON.stringify(requestBody)
        })

        const response = await this.bedrockClient.send(command)
        const responseBody = JSON.parse(new TextDecoder().decode(response.body))

        console.log(`✅ Succès avec le modèle: ${modelId}`)
        this.selectedModel = modelId // Sauvegarder le modèle qui fonctionne

        return responseBody.content[0].text

      } catch (error: any) {
        console.log(`❌ Échec avec ${modelId}:`, error.message)
        lastError = error

        // Si c'est un problème de modèle non trouvé, continuer avec le suivant
        if (error.message?.includes('ValidationException') ||
            error.message?.includes('ResourceNotFound') ||
            error.message?.includes('Access denied')) {
          continue
        }

        // Si c'est un autre type d'erreur, l'enregistrer mais continuer
        if (!error.message?.includes('throttle') && !error.message?.includes('rate')) {
          continue
        }

        // Si erreur de throttling, attendre un peu et réessayer
        await new Promise(resolve => setTimeout(resolve, 1000))
        continue
      }
    }

    // Si tous les modèles ont échoué
    console.error('❌ Tous les modèles AWS Bedrock ont échoué')
    throw new Error(`AWS Bedrock indisponible. Dernière erreur: ${lastError?.message}`)
  }

  // Méthode utilitaire pour tester la configuration
  async testConfiguration(): Promise<{ success: boolean, method: string, model: string, error?: string }> {
    try {
      const testMessages: ClaudeMessage[] = [
        { role: 'user', content: 'Bonjour, pouvez-vous répondre "Test réussi" ?' }
      ]

      const response = await this.generateResponse(testMessages, 'Vous êtes un assistant médical.', 50, 0.1)

      return {
        success: true,
        method: this.useDirectAPI ? 'Anthropic API' : 'AWS Bedrock',
        model: this.selectedModel || 'unknown',
      }
    } catch (error: any) {
      return {
        success: false,
        method: this.useDirectAPI ? 'Anthropic API' : 'AWS Bedrock',
        model: this.selectedModel || 'unknown',
        error: error.message
      }
    }
  }
}

// Instance singleton
let claudeClientInstance: ClaudeClient | null = null

export function getClaudeClient(): ClaudeClient {
  if (!claudeClientInstance) {
    claudeClientInstance = new ClaudeClient()
  }
  return claudeClientInstance
}

// Fonction helper pour compatibilité
export async function callClaude(
  prompt: string,
  systemPrompt?: string,
  maxTokens: number = 2000
): Promise<string> {
  const client = getClaudeClient()
  const messages: ClaudeMessage[] = [
    { role: 'user', content: prompt }
  ]

  return await client.generateResponse(messages, systemPrompt, maxTokens)
}

export default ClaudeClient